// order lists — helpers for the "Order Lists" folder in Info / Files, the online counterpart of
// the yard's local "PRODUCTION - Order Lists" directory. One season folder per production year
// (e.g. "General 2026-2027") holds the master order sheet plus a workbook per vessel.
//
// readWorkbookGrid turns any of those .xlsx/.csv files into plain string rows so the browser can
// render them without downloading. It is a thin wrapper over SheetJS (`XLSX` injected so it can be
// unit-tested); the season helpers below are pure.
import { fold, hasText } from '../lib/search.js';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** file extensions the in-browser viewer can render. */
const SPREADSHEET_RE = /\.(xlsx|xlsm|xlsb|xls|csv)$/i;
export const isSpreadsheet = (name: string): boolean => SPREADSHEET_RE.test(name);

/** the year a season id starts in, e.g. "26-27" → 2026 (NaN-safe, returns -1 when unparseable). */
export function seasonStartYear(id: string): number {
  const yy = parseInt((id || '').split('-')[0] ?? '', 10);
  return Number.isNaN(yy) ? -1 : 2000 + yy;
}

// the folder name for a season ("26-27" → "General 2026-2027") lives with the order files, which the worker
// files there too
export { seasonFolderLabel } from '@technohull/shared';

/** the season id embedded in a folder name: "General 2026-2027" → "26-27" ('' when none is found). */
export function seasonIdFromFolderLabel(name: string): string {
  const m = /(\d{4})\s*-\s*(\d{4})/.exec(name || '');
  if (m) return `${Number(m[1]) % 100}-${Number(m[2]) % 100}`;
  // also accept a bare "yy-yy" folder name
  const m2 = /\b(\d{2})\s*-\s*(\d{2})\b/.exec(name || '');
  return m2 ? `${m2[1]}-${m2[2]}` : '';
}

// ── workbook → grid ─────────────────────────────────────────────────────────────
export interface SheetGrid {
  name: string;
  /** the first row (column headers, as written in the workbook). */
  header: string[];
  /** every data row below the header, each padded to `header.length`. */
  rows: string[][];
}
export interface WorkbookGrid {
  sheets: SheetGrid[];
}

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const pad2 = (n: number) => String(n).padStart(2, '0');
const isoOf = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

/** display text of a single cell — dates become ISO, everything else its trimmed string form. */
function toText(v: unknown): string {
  if (v == null) return '';
  if (v instanceof Date) return isoOf(v);
  return String(v).trim();
}

/**
 * Parse a workbook into per-sheet grids of display strings. `XLSX` is the SheetJS module.
 * Cells are read raw (dates arrive as Date objects thanks to cellDates) and normalised by toText, so
 * a date becomes ISO "yyyy-mm-dd" rather than an ambiguous locale format. Fully-empty rows are
 * dropped and every sheet is squared off to its widest used column so the table renders cleanly.
 */
export function readWorkbookGrid(XLSX: any, buf: ArrayBuffer): WorkbookGrid {
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const sheets: SheetGrid[] = [];
  for (const name of (wb.SheetNames as string[]) ?? []) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const aoa: unknown[][] = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      defval: '',
      blankrows: false,
      raw: true,
    });
    const grid = aoa.map((r) => (Array.isArray(r) ? r.map(toText) : []));

    // widest column that actually carries a value across the whole sheet
    let cols = 0;
    for (const r of grid) {
      for (let i = r.length - 1; i >= 0; i--) {
        if (r[i] !== '') {
          if (i + 1 > cols) cols = i + 1;
          break;
        }
      }
    }
    // square every row to `cols`
    const squared = grid.map((r) => {
      const out = r.slice(0, cols);
      while (out.length < cols) out.push('');
      return out;
    });
    // trim empty rows that linger at the end
    while (squared.length) {
      const last = squared[squared.length - 1];
      if (last && last.every((c) => c === '')) squared.pop();
      else break;
    }
    if (squared.length === 0 && cols === 0) {
      sheets.push({ name, header: [], rows: [] });
      continue;
    }
    const [header, ...rows] = squared;
    sheets.push({ name, header: header ?? [], rows });
  }
  return { sheets };
}

/** true when any cell in the row contains `q`, ignoring case and accents. Empty query matches everything. */
export function rowMatches(row: string[], q: string): boolean {
  if (!q) return true;
  return hasText(fold(q), ...row);
}

// ── SAP-prefixed file names ──────────────────────────────────────────────────────
// Order-list workbooks are named "<SAP> - <VESSEL>.xlsx" (e.g. "9100912 - FANDY.xlsx"). Files that
// pre-date a vessel's SAP were filed as just "<VESSEL>.xlsx"; once the SAP is known we want to rename
// them so a "refresh from folder" doesn't leave two copies.

export interface FileNameParts { sap: string; core: string; ext: string }

/** split a filename into an optional SAP prefix, the vessel-name core, and the extension.
 *  "9100912 - FANDY.xlsx" → {sap:'9100912', core:'FANDY', ext:'.xlsx'}; "JASNA.xlsx" → {sap:'', core:'JASNA', ext:'.xlsx'}. */
export function parseFileName(name: string): FileNameParts {
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot) : '';
  const base = (dot > 0 ? name.slice(0, dot) : name).trim();
  const m = /^([0-9][0-9A-Za-z()\-/ ]*?)\s+-\s+(.+)$/.exec(base);
  if (m && /\d{4,}/.test(m[1] ?? '')) return { sap: (m[1] ?? '').trim(), core: (m[2] ?? '').trim(), ext };
  return { sap: '', core: base, ext };
}

/** normalise a vessel name for matching: upper-case, collapse whitespace. */
export const normName = (s: string): string => s.toUpperCase().replace(/\s+/g, ' ').trim();
/** does a string look like a real SAP number (≥4 digits) rather than a placeholder like "?" or "3"? */
export const looksLikeSap = (s: string): boolean => /\d{4,}/.test(s);

export interface SapRenameProposal {
  /** the local file's current name. */
  originalName: string;
  /** the name it should be filed under, with the SAP prefix. */
  newName: string;
  /** existing online files (same vessel, no/other SAP) this upload should replace. */
  supersedes: string[];
}

/** work out which of the picked local files should gain a SAP prefix (and which stale online copies
 *  they replace), matching each file's vessel name to a known SAP. `sapByName` maps a normalised
 *  vessel name to its SAP. Returns only files that need a change. Pure — unit-tested. */
export function proposeSapRenames(
  localNames: string[],
  onlineNames: string[],
  sapByName: Map<string, string>,
): SapRenameProposal[] {
  const online = onlineNames.map((n) => ({ n, ...parseFileName(n) }));
  const out: SapRenameProposal[] = [];
  for (const local of localNames) {
    const p = parseFileName(local);
    if (!p.core) continue;
    const targetSap = looksLikeSap(p.sap) ? p.sap : (sapByName.get(normName(p.core)) ?? '');
    if (!targetSap) continue;
    const newName = `${targetSap} - ${p.core}${p.ext}`;
    const supersedes = online
      .filter((o) => normName(o.core) === normName(p.core) && o.sap !== targetSap && o.n !== newName)
      .map((o) => o.n);
    if (newName !== local || supersedes.length) out.push({ originalName: local, newName, supersedes });
  }
  return out;
}
